import * as React from 'react'
import { useMemo } from 'react'
import { Checkbox, mergeClasses, Text, Tooltip } from '@fluentui/react-components'
import PersonAvatar from '../../../components/PersonAvatar'
import { Link } from 'react-router-dom'
import { useOverviewStyles } from '../../../pages/BaseOverview/BaseOverview'
import { DateFormats } from '../../../helpers/dateHelper'
import { useTranslation } from 'react-i18next'
import MoneyText from '../../../components/Text/Currency/MoneyText'
import { BillableLabels } from '../../../components/TagPicker/BillablePicker'
import { InvoiceStatusLabels } from '../../../components/TagPicker/InvoiceStatusPicker'
import { BaseTimeLineOverviewProps, useItemStyles } from './BaseTimeOverview'
import FormattedDate from '../../../components/Date/FormattedDate'
import TimeText from '../../../components/Text/Time/TimeText'
import { CheckmarkCircleRegular, DismissRegular } from '@fluentui/react-icons'
import { Billable } from '../../../config/registration/timeConfig'

function TurnoverField(props: BaseTimeLineOverviewProps) {
    const { t: translate } = useTranslation()

    return (
        <Tooltip
            relationship={'label'}
            content={
                <div>
                    <div>
                        {translate('registration.rate')}: <MoneyText value={props.item.rate} inline />
                    </div>
                    <div>
                        {translate('time.billable')}: {translate(BillableLabels.get(props.item.billable).name)}
                    </div>
                </div>
            }
            withArrow
        >
            <div>
                {/*Apply a diff as the Tooltip needs a html component other than span.*/}
                <MoneyText value={props.item.turnover} align="right" />
            </div>
        </Tooltip>
    )
}

function InvoiceTurnoverField(props: BaseTimeLineOverviewProps) {
    const { t: translate } = useTranslation()

    return (
        <Tooltip
            relationship={'label'}
            content={
                <>
                    <div>
                        {translate('invoice.edit.form.header.number')}: {props.item.invoice.number}
                    </div>
                    <div>
                        {translate('invoice.status.label')}: {translate(InvoiceStatusLabels.get(props.item.invoice.status)?.name, { count: 0 })}
                    </div>
                </>
            }
            withArrow
        >
            <Link to={`/invoice/${props.item.invoice.id}/edit`}>
                <MoneyText value={props.item.invoiceTurnover} align="right" />
            </Link>
        </Tooltip>
    )
}

export default function TimeListItem(props: BaseTimeLineOverviewProps) {
    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()

    const isBillable = useMemo(() => props.item.billable === Billable.Billable, [props.item])

    if (props.viewportMedium) {
        return (
            <>
                {props.onItemSelected && (
                    <Checkbox
                        className={overviewStyles.centerItems}
                        checked={props.isItemSelected?.(props.item)}
                        disabled={!props.isItemSelectable?.(props.item)}
                        onChange={(_, data) => props.onItemSelected(data, props.item)}
                    />
                )}
                <PersonAvatar name={props.item.user.fullName} color={'colorful'} size={24} />

                <Tooltip relationship={'label'} content={props.item.description} withArrow>
                    <Text className={itemStyles.listTimeItemOverflow}>{props.item.description}</Text>
                </Tooltip>
                {props.onItemSelected && <div></div>}
                <div>
                    <FormattedDate date={props.item.date} format={DateFormats.Full} />
                    <div>
                        <TimeText value={props.item.duration} />
                    </div>
                </div>
                <div className={props.viewportTiny ? itemStyles.listTimeItemTinyBottomRow : itemStyles.listTimeItemSmallBottomRow}>
                    {!props.hideFileField ? (
                        <div>
                            <Tooltip relationship={'label'} content={props.item.file.name} withArrow>
                                <Link key={props.item.id} to={`/file/${props.item.file.id}`}>
                                    <Text size={200}>{props.item.file.reference}</Text>
                                </Link>
                            </Tooltip>
                        </div>
                    ) : (
                        <div></div>
                    )}
                    <TurnoverField {...props} />
                    {!props.viewportTiny && !props.hideInvoiceField && props.item.invoice && <InvoiceTurnoverField {...props} />}
                    {!props.viewportNarrow && !props.hideInvoiceField && !props.item.invoice && (
                        <div
                            className={mergeClasses(
                                overviewStyles.alignEnd,
                                itemStyles.billableIconSize,
                                isBillable ? itemStyles.colorGreen : itemStyles.colorRed,
                            )}
                        >
                            {isBillable ? <CheckmarkCircleRegular /> : <DismissRegular />}
                        </div>
                    )}
                </div>
            </>
        )
    }

    return (
        <>
            {props.onItemSelected && (
                <Checkbox
                    className={overviewStyles.centerItems}
                    checked={props.isItemSelected?.(props.item)}
                    disabled={!props.isItemSelectable?.(props.item)}
                    onChange={(_, data) => props.onItemSelected(data, props.item)}
                />
            )}
            <PersonAvatar name={props.item.user.fullName} color={'colorful'} size={24} />
            <div>
                <FormattedDate date={props.item.date} format={DateFormats.Full} />
            </div>
            <div>
                <TimeText value={props.item.duration} />
            </div>
            <Tooltip relationship={'label'} content={props.item.description} withArrow>
                <Text className={itemStyles.listTimeItemOverflow}>{props.item.description}</Text>
            </Tooltip>
            {!props.hideFileField ? (
                <div>
                    <Tooltip relationship={'label'} content={props.item.file.name} withArrow>
                        <Link key={props.item.id} to={`/file/${props.item.file.id}`}>
                            <Text size={200}>{props.item.file.reference}</Text>
                        </Link>
                    </Tooltip>
                </div>
            ) : (
                <div></div>
            )}
            <TurnoverField {...props} />
            {!props.hideInvoiceField && props.item.invoice && <InvoiceTurnoverField {...props} />}
            {!props.hideInvoiceField && !props.item.invoice && (
                <div
                    className={mergeClasses(
                        overviewStyles.alignEnd,
                        itemStyles.billableIconSize,
                        isBillable ? itemStyles.colorGreen : itemStyles.colorRed,
                    )}
                >
                    {props.item.billable === Billable.Billable ? <CheckmarkCircleRegular /> : <DismissRegular />}
                </div>
            )}
        </>
    )
}
